/**
 * ThreadHeading - Thread subject heading with back navigation and context actions.
 *
 * - Firm view: "Invite Users" button on the right (when hasPermission)
 * - Client portal: "Mark as Unread" button on the right
 */

import React, { memo } from 'react';
import PropTypes from 'prop-types';
import { Tag } from 'primereact/tag';
import { Button } from 'primereact/button';
import { useHistory } from 'react-router-dom';

function ThreadHeading({ messageDetails, handleMarkAsUnRead, isFromClientPortal, hasPermission, onInviteClick }) {
  const history = useHistory();

  return (
    <div
      className={`d-flex mb-1 align-items-center justify-content-between ${isFromClientPortal ? 'mt-2' : ''}`}
      role="region"
      aria-label="Thread header"
    >
      <div className="d-flex align-items-center overflow-hidden">
        <a
          href={`/#/cases/${messageDetails?.case?.case_id || ''}/messages`}
          onClick={(e) => {
            e.preventDefault();
            history.goBack();
          }}
          className="text-decoration-none flex-shrink-0"
          aria-label="Go back to message list"
        >
          <i className="icon-back icon-box me-3 pointer" aria-hidden="true" />
        </a>

        <h1 className="title gray-800 mb-0 fs-3 fs-md-1 ml-1 text-truncate" title={messageDetails?.subject || 'Untitled Thread'}>
          Thread : {messageDetails?.subject || 'Untitled Thread'}
        </h1>

        {messageDetails?.is_new && (
          <span style={{ marginLeft: '0.5rem' }} className="flex-shrink-0">
            <Tag className="p-mr-2 tag-case" value="New" rounded aria-label="New message thread" />
          </span>
        )}
      </div>

      {isFromClientPortal && (
        <Button
          label="Mark as Unread"
          className="btn-outline p-button-secondary outline py-1 px-3 flex-shrink-0"
          type="button"
          style={{ overflow: 'inherit' }}
          onClick={(e) => handleMarkAsUnRead(e, messageDetails, true)}
          aria-label="Mark this thread as unread"
        />
      )}

      {!isFromClientPortal && hasPermission && (
        <Button
          label="Invite Users"
          icon="pi pi-user-plus"
          className="p-button-primary flex-shrink-0"
          onClick={onInviteClick}
          aria-label="Invite users to this conversation"
        />
      )}
    </div>
  );
}

ThreadHeading.propTypes = {
  messageDetails: PropTypes.shape({
    subject: PropTypes.string,
    is_new: PropTypes.bool,
    case: PropTypes.shape({
      case_id: PropTypes.string,
    }),
  }),
  handleMarkAsUnRead: PropTypes.func,
  isFromClientPortal: PropTypes.bool,
  hasPermission: PropTypes.bool,
  onInviteClick: PropTypes.func,
};

ThreadHeading.defaultProps = {
  messageDetails: null,
  handleMarkAsUnRead: () => {},
  isFromClientPortal: false,
  hasPermission: false,
  onInviteClick: () => {},
};

export default memo(ThreadHeading);
